import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import MarketListWizard from '../components/MarketListWizard';
import QuickListEntryModal from '../components/QuickListEntryModal';
import ShoppingListDetailModal from '../components/ShoppingListDetailModal';
import { API_BASE_URL } from '../config';
import { motion, AnimatePresence } from 'framer-motion';
import AlternativeProductModal from '../components/AlternativeProductModal';
import Swal from 'sweetalert2';
import { formatCurrency } from '../utils/formatUtils';
import { resolveShoppingDisplayAmountMeta } from '../shared/shoppingDisplayText';
import LocationNudge from '../components/LocationNudge';

function getShoppingPreviewAmount(item = {}) {
    return resolveShoppingDisplayAmountMeta(item).display;
}

function isShoppingListPreview(list) {
    return Boolean(list?.isPreview || list?.viewContract === 'preview');
}

function resolveProjectionTotalPrice(list = {}) {
    const cached = Number(list?.totalPriceCached ?? 0);
    if (cached > 0) return cached;

    if (!Array.isArray(list?.items)) return 0;
    return list.items.reduce((sum, item) => {
        return sum + (Number(item?.price ?? 0) || Number(item?.priceAtTime ?? 0) || 0);
    }, 0);
}

function getListStatusLabel(status) {
    if (status === 'completed') return 'Tamamlandı';
    if (status === 'awaiting_owner_confirmation') return 'Onay Bekliyor';
    return 'Bekliyor';
}

function getListStatusClassName(status) {
    if (status === 'completed') {
        return 'bg-green-50 text-green-600 border-green-100 dark:bg-green-900/20 dark:border-green-900/30';
    }
    if (status === 'awaiting_owner_confirmation') {
        return 'bg-blue-50 text-blue-600 border-blue-100 dark:bg-blue-900/20 dark:border-blue-900/30';
    }
    return 'bg-orange-50 text-orange-600 border-orange-100 dark:bg-orange-900/20 dark:border-orange-900/30';
}

function buildAlakartPreviewList(payload = {}) {
    const previewName = payload?.listName || 'Alakart 1. Hafta';
    const previewItems = Array.isArray(payload.items) ? payload.items.slice(0, 5) : [];

    return {
        id: `alakart-preview-${previewName}`,
        name: previewName,
        status: 'pending',
        createdAt: new Date().toISOString(),
        totalPriceCached: 0,
        totalItemsCached: Array.isArray(payload.items) ? payload.items.length : 0,
        items: previewItems,
        isPreview: true,
        viewContract: 'preview',
        previewSource: 'ALAKART_COMMIT'
    };
}

function mergeProjectionLists(previousLists = [], fetchedLists = [], isLoadMore = false) {
    const previews = previousLists.filter((list) => isShoppingListPreview(list));
    const nextLists = isLoadMore ? [...previousLists, ...fetchedLists] : [...fetchedLists];

    if (!previews.length) return nextLists;

    const materializedNames = new Set(
        nextLists
            .filter((list) => !isShoppingListPreview(list))
            .map((list) => String(list.name || '').trim())
            .filter(Boolean)
    );

    const unresolvedPreviews = previews.filter((preview) => {
        const previewName = String(preview.name || '').trim();
        return previewName && !materializedNames.has(previewName);
    });

    if (!unresolvedPreviews.length) return nextLists;

    return isLoadMore
        ? [...nextLists, ...unresolvedPreviews]
        : [...unresolvedPreviews, ...nextLists];
}

const MyShoppingLists = () => {
    const navigate = useNavigate();
    const [isMarketWizardOpen, setIsMarketWizardOpen] = useState(false);
    const [isQuickEntryOpen, setIsQuickEntryOpen] = useState(false);
    const [isHistoryViewOpen, setIsHistoryViewOpen] = useState(false);
    const [wizardInitialTab, setWizardInitialTab] = useState('stoklar');
    const [lists, setLists] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedList, setSelectedList] = useState(null);
    const [selectedListAutoReview, setSelectedListAutoReview] = useState(false);
    const [sharingList, setSharingList] = useState(null);
    const routerLocation = useLocation();


    const [cursor, setCursor] = useState(null);
    const [hasMore, setHasMore] = useState(true);

    const fetchLists = async (isLoadMore = false) => {
        if (isLoading) return;
        setIsLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            const url = new URL(`${API_BASE_URL}/api/shopping/lists`);
            if (isLoadMore && cursor) {
                url.searchParams.append('cursor', cursor);
            }
            url.searchParams.append('limit', '10');

            const res = await fetch(url.toString(), {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                const normalizedData = Array.isArray(data) ? data.map((list) => ({
                    ...list,
                    isPreview: false,
                    viewContract: list?.viewContract || 'projection'
                })) : [];
                if (isLoadMore) {
                    setLists((prev) => mergeProjectionLists(prev, normalizedData, true));
                } else {
                    setLists((prev) => mergeProjectionLists(prev, normalizedData, false));
                }
                
                if (normalizedData.length > 0) {
                    setCursor(normalizedData[normalizedData.length - 1].id);
                    setHasMore(normalizedData.length === 10);
                } else {
                    setHasMore(false);
                }
            }
        } catch (error) {
            console.error('Failed to fetch lists:', error);
        } finally {
            setIsLoading(false);
        }
    };

    React.useEffect(() => {
        // Reset and fetch on mount
        setCursor(null);
        setHasMore(true);
        fetchLists(false);

        // Listen for visibility change
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                setCursor(null);
                fetchLists(false);
            }
        };
        const handleShoppingRefresh = () => {
            setCursor(null);
            setHasMore(true);
            fetchLists(false);
        };
        const handleShoppingListUpdated = (event) => {
            const payload = event?.detail || {};
            if (payload?.source !== 'ALAKART_COMMIT' || !Array.isArray(payload.items)) {
                handleShoppingRefresh();
                return;
            }

            const previewList = buildAlakartPreviewList(payload);

            setLists((prev) => {
                const filtered = prev.filter((list) => {
                    if (list.name !== previewList.name) return true;
                    return !isShoppingListPreview(list);
                });
                return [previewList, ...filtered];
            });
            handleShoppingRefresh();
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('shoppingListUpdated', handleShoppingRefresh);
        window.addEventListener('Alakart_SYNC', handleShoppingRefresh);
        window.addEventListener('realtime:inventory-updated', handleShoppingRefresh);
        window.addEventListener('shopping:list-updated', handleShoppingListUpdated);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('shoppingListUpdated', handleShoppingRefresh);
            window.removeEventListener('Alakart_SYNC', handleShoppingRefresh);
            window.removeEventListener('realtime:inventory-updated', handleShoppingRefresh);
            window.removeEventListener('shopping:list-updated', handleShoppingListUpdated);
        };
    }, []);

    React.useEffect(() => {
        const routeState = routerLocation.state || {};
        if (lists.length > 0 && routeState.openListId) {
            const listToOpen = lists.find((l) => !isShoppingListPreview(l) && l.id === Number(routeState.openListId));
            if (listToOpen) {
                setSelectedList(listToOpen);
                setSelectedListAutoReview(Boolean(routeState.autoOpenReview));
                
                // Clear state to prevent re-opening on manual refresh
                window.history.replaceState({}, document.title);
            }
        }
    }, [lists, routerLocation.state]);

    React.useEffect(() => {
        if (isHistoryViewOpen) {
            fetchLists();
        }
    }, [isHistoryViewOpen]);

    React.useEffect(() => {
        if (routerLocation.state?.openHistory || routerLocation.state?.openListId) {
            setIsHistoryViewOpen(true);
        }
    }, [routerLocation.state]);

    React.useEffect(() => {
        if (routerLocation.state?.openSmartList) {
            setWizardInitialTab('akilli');
            setIsMarketWizardOpen(true);
            window.history.replaceState({}, document.title);
        }
    }, [routerLocation.state]);

    // Fonksiyonel Aksiyonlar
    const handleQuickList = () => {
        setIsQuickEntryOpen(true);
    };

    const handleSmartList = () => {
        setWizardInitialTab('akilli');
        setIsMarketWizardOpen(true);
    };

    const handleDeleteList = async (listId, e) => {
        if (e) e.stopPropagation();

        // Optimistic UI Update: Immediately remove from screen
        const originalLists = [...lists];
        setLists(prev => prev.filter(l => l.id !== listId));
        if (selectedList?.id === listId) {
            setSelectedList(null);
        }

        try {
            const token = localStorage.getItem('authToken');
            const res = await fetch(`${API_BASE_URL}/api/shopping/lists/${listId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (res.ok) {
                // Sync TopBar and Sidebar badges immediately
                window.dispatchEvent(new Event('Alakart_SYNC'));
                window.dispatchEvent(new Event('shoppingListUpdated'));
            } else {
                const errorData = await res.json().catch(() => ({}));
                console.error('Failed to delete list on backend', errorData);
                setLists(originalLists);
                alert(`Hata: ${errorData.error || errorData.details || 'Liste silinemedi.'}`);
            }
        } catch (error) {
            console.error('Error deleting list:', error);
            setLists(originalLists);
            alert(`Bağlantı hatası: ${error.message}`);
        }
    };

    const handleViewHistory = () => {
        setIsHistoryViewOpen(true);
    };

    return (
        <div className="flex flex-col w-full items-center justify-center p-6 sm:p-12">
            <div className="w-full max-w-6xl space-y-12">

                <LocationNudge />

                {/* Header Section */}
                <div className="relative text-center space-y-6">
                    <motion.div 
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="inline-block px-4 py-1.5 bg-gradient-to-r from-primary/10 to-blue-500/10 rounded-full border border-primary/20 backdrop-blur-sm mb-2"
                    >
                        <span className="text-[10px] font-black uppercase tracking-[0.3em] text-primary">Mutfak Kontrol Merkezi</span>
                    </motion.div>
                    <h1 className="text-5xl sm:text-7xl font-black tracking-tighter text-[#1e293b] dark:text-white leading-none">
                        Liste <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-orange-500 to-amber-500">Yönetimi</span>
                    </h1>
                    <p className="text-xl text-[#64748b] dark:text-slate-400 max-w-2xl mx-auto font-medium leading-relaxed">
                        Mutfak ekonomini ve tarif hazırlıklarını tek bir yerden, <br className="hidden sm:block" />
                        en akıllı şekilde yönet şefim.
                    </p>
                </div>

                {/* Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">

                    {/* Hızlı Liste */}
                    <button
                        onClick={handleQuickList}
                        className="group flex flex-col items-center text-center p-10 bg-white dark:bg-slate-800 border border-[#f1f5f9] dark:border-slate-700 rounded-[2rem] shadow-[0_20px_50px_-12px_rgba(0,0,0,0.05)] transition-all duration-300 ease-out hover:-translate-y-2 hover:shadow-[0_30px_60px_-12px_rgba(238,43,43,0.08)] hover:border-[#ee2b2b]/10 dark:hover:border-[#ee2b2b]/30 relative overflow-hidden"
                    >
                        <div className="absolute top-0 right-0 p-4 opacity-5 dark:opacity-10 group-hover:opacity-10 dark:group-hover:opacity-20 transition-opacity">
                            <span className="material-symbols-outlined text-8xl dark:text-white">list_alt</span>
                        </div>
                        <div className="size-20 bg-[#f8fafc] dark:bg-slate-700 rounded-3xl flex items-center justify-center mb-8 group-hover:bg-[#ee2b2b]/5 dark:group-hover:bg-[#ee2b2b]/10 transition-colors">
                            <span className="material-symbols-outlined text-4xl text-[#1e293b] dark:text-white group-hover:text-[#ee2b2b] transition-colors">flash_on</span>
                        </div>
                        <h3 className="text-2xl font-bold mb-4 text-[#1e293b] dark:text-slate-100 transition-colors">Hızlı Liste Oluştur</h3>
                        <p className="text-[#64748b] dark:text-slate-400 leading-relaxed transition-colors">
                            Pratik bir şekilde malzemelerini manuel olarak ekle ve listeni hemen hazırla.
                        </p>
                        <div className="mt-8 flex items-center gap-2 text-[#ee2b2b] font-bold">
                            <span>Başla</span>
                            <span className="material-symbols-outlined text-sm">arrow_forward</span>
                        </div>
                    </button>

                    {/* Akıllı Liste */}
                    <button
                        onClick={handleSmartList}
                        className="group flex flex-col items-center text-center p-10 bg-white dark:bg-slate-800 border-2 border-[#ee2b2b]/20 dark:border-[#ee2b2b]/40 rounded-[2rem] shadow-[0_20px_50px_-12px_rgba(0,0,0,0.05)] transition-all duration-300 ease-out hover:-translate-y-2 hover:shadow-[0_30px_60px_-12px_rgba(238,43,43,0.08)] hover:border-[#ee2b2b]/10 dark:hover:border-[#ee2b2b]/60 relative overflow-hidden scale-105 z-10"
                    >
                        <div className="absolute -top-4 -right-4 size-24 bg-[#ee2b2b]/5 dark:bg-[#ee2b2b]/10 rounded-full blur-2xl"></div>
                        <div className="size-20 bg-[#ee2b2b]/10 dark:bg-[#ee2b2b]/20 rounded-3xl flex items-center justify-center mb-8 group-hover:bg-[#ee2b2b] transition-colors">
                            <span className="material-symbols-outlined text-4xl text-[#ee2b2b] group-hover:text-white transition-colors">inventory_2</span>
                        </div>
                        <h3 className="text-2xl font-bold mb-4 text-[#1e293b] dark:text-slate-100 transition-colors">Akıllı Liste Oluştur</h3>
                        <p className="text-[#64748b] dark:text-slate-400 leading-relaxed transition-colors">
                            Stoklarına göre hızlı tükenenlerin işaretli olduğu akıllı listeler oluştur.
                        </p>
                        <div className="mt-8 flex items-center gap-2 text-[#ee2b2b] font-bold">
                            <span>Hemen Oluştur</span>
                            <span className="material-symbols-outlined text-sm">magic_button</span>
                        </div>
                    </button>

                    {/* Listelerim */}
                    <button
                        onClick={handleViewHistory}
                        className="group flex flex-col items-center text-center p-10 bg-white dark:bg-slate-800 border-2 border-slate-100 dark:border-slate-700 rounded-[3rem] shadow-xl hover:shadow-2xl transition-all duration-500 ease-out hover:-translate-y-2 hover:border-slate-300 dark:hover:border-slate-500 relative overflow-hidden"
                    >
                        <div className="absolute inset-0 bg-gradient-to-br from-slate-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                        
                        {/* List Count Badge */}
                        {lists.filter(l => l.status !== 'completed').length > 0 && (
                            <motion.div 
                                initial={{ scale: 0, rotate: -20 }}
                                animate={{ scale: 1, rotate: 12 }}
                                className="absolute top-8 right-8 w-12 h-12 bg-[#1e293b] dark:bg-white text-white dark:text-[#1e293b] rounded-2xl flex items-center justify-center font-black text-xl shadow-2xl border-4 border-white dark:border-slate-800 z-20 group-hover:scale-110 group-hover:rotate-0 transition-all"
                            >
                                {lists.filter(l => l.status !== 'completed').length}
                            </motion.div>
                        )}

                        <div className="size-24 bg-slate-50 dark:bg-slate-700/50 rounded-[2rem] flex items-center justify-center mb-10 group-hover:scale-110 transition-transform duration-500">
                            <span className="material-symbols-outlined text-5xl text-slate-400 group-hover:text-slate-900 dark:group-hover:text-white transition-colors">bookmarks</span>
                        </div>
                        <h3 className="text-2xl font-black mb-4 text-[#1e293b] dark:text-slate-100 uppercase tracking-tight">Kayıtlı Listeler</h3>
                        <p className="text-[#64748b] dark:text-slate-400 font-medium leading-relaxed">
                            Aktif alışveriş listelerini ve planlamalarını tek yerden yönet.
                        </p>
                        <div className="mt-10 flex items-center gap-2 text-slate-400 dark:text-slate-500 group-hover:text-slate-900 dark:group-hover:text-white font-black uppercase tracking-widest text-[10px] transition-all">
                            <span>Görüntüle</span>
                            <span className="material-symbols-outlined text-base">arrow_forward</span>
                        </div>
                    </button>

                </div>

                {/* Footer Info */}


            </div>

            {/* Modals */}
            <MarketListWizard
                isOpen={isMarketWizardOpen}
                onClose={() => setIsMarketWizardOpen(false)}
                initialTab={wizardInitialTab}
                onSuccess={fetchLists}
            />

            <QuickListEntryModal
                isOpen={isQuickEntryOpen}
                onClose={() => setIsQuickEntryOpen(false)}
                onFinish={() => {
                    fetchLists();
                }}
            />

            {/* Geçmiş Görünümü Modali */}
            <AnimatePresence>
                {isHistoryViewOpen && (
                    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-md flex items-center justify-center p-4 md:p-6">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: 30 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 30 }}
                            className="bg-white dark:bg-slate-900 rounded-[2.5rem] w-full max-w-4xl max-h-[85vh] flex flex-col shadow-2xl relative border border-white/50 dark:border-slate-800 overflow-hidden"
                        >
                            <div className="p-8 pb-4 border-b border-gray-100 dark:border-slate-800 flex justify-between items-center">
                                <div>
                                    <h2 className="text-2xl font-black text-gray-900 dark:text-white flex items-center gap-3">
                                        <span className="material-symbols-outlined text-primary">history</span>
                                        Geçmiş Listelerim
                                    </h2>
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">Son oluşturulan listelerin</p>
                                </div>
                                <button
                                    onClick={() => setIsHistoryViewOpen(false)}
                                    className="w-12 h-12 flex items-center justify-center bg-gray-100 dark:bg-slate-800 rounded-full text-slate-400 hover:text-primary hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors"
                                >
                                    <span className="material-symbols-outlined">close</span>
                                </button>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-8 overflow-y-auto no-scrollbar">

                                {/* Lists */}
                                {/* Lists */}
                                {isLoading ? (
                                    <div className="col-span-full text-center py-20">
                                        <div className="animate-spin size-10 border-4 border-primary/30 border-t-primary rounded-full mx-auto mb-4"></div>
                                        <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Yükleniyor...</p>
                                    </div>
                                ) : lists.filter(l => l.status !== 'completed').length === 0 ? (
                                    <div className="col-span-full text-center py-20 grayscale opacity-50">
                                        <span className="material-symbols-outlined text-6xl mb-4">shopping_basket</span>
                                        <p className="text-sm font-bold text-gray-400 uppercase tracking-widest italic">Henüz bir listen yok</p>
                                    </div>
                                ) : (
                                    <>
                                        {lists.filter(l => l.status !== 'completed').map((list, listIdx) => (
                                            <motion.div
                                                key={list.id || `list-${listIdx}`}
                                                whileHover={{ scale: 1.02, y: -5 }}
                                                    onClick={() => {
                                                    if (isShoppingListPreview(list)) return;
                                                    setSelectedList(list);
                                                    setSelectedListAutoReview(false);
                                                }}
                                                className="group bg-gray-50 dark:bg-slate-800/50 p-6 rounded-3xl border border-transparent hover:border-primary/20 hover:bg-white dark:hover:bg-slate-800 shadow-sm hover:shadow-xl transition-all relative overflow-hidden cursor-pointer flex flex-col justify-between"
                                            >
                                                {!isShoppingListPreview(list) && (
                                                    <div className="absolute top-4 right-4 z-10 opacity-0 group-hover:opacity-100 transition-all transform scale-90 group-hover:scale-100">
                                                        <button
                                                            onClick={(e) => handleDeleteList(list.id, e)}
                                                            className="w-10 h-10 flex items-center justify-center bg-white dark:bg-slate-900 border border-red-100 dark:border-red-900/30 text-red-500 rounded-xl hover:bg-red-500 hover:text-white transition-all shadow-lg"
                                                            title="Listeyi Sil"
                                                        >
                                                            <span className="material-symbols-outlined text-[18px]">delete</span>
                                                        </button>
                                                    </div>
                                                )}

                                                <div>
                                                    <div className="flex justify-between items-start mb-5 relative z-10">
                                                        <div className="flex-1">
                                                            <h4 className="text-xl font-black text-gray-900 dark:text-white group-hover:text-primary transition-colors tracking-tight leading-tight mb-2 pr-10">{list.name}</h4>
                                                            <div className="flex items-center gap-3">
                                                                <div className="flex items-center gap-1.5 px-2 py-1 bg-slate-100 dark:bg-slate-700/50 rounded-lg">
                                                                    <span className="material-symbols-outlined text-[14px] text-slate-400">calendar_today</span>
                                                                    <span className="text-[10px] font-black text-slate-500 dark:text-slate-300 uppercase tracking-widest leading-none">
                                                                        {new Date(list.createdAt).toLocaleDateString('tr-TR')}
                                                                    </span>
                                                                </div>
                                                                <div className={`px-2.5 py-1 rounded-lg text-[9px] font-black uppercase tracking-[0.1em] border ${list.status === 'completed' 
                                                                    ? 'bg-green-50 text-green-600 border-green-100 dark:bg-green-900/20 dark:border-green-900/30' 
                                                                    : 'bg-orange-50 text-orange-600 border-orange-100 dark:bg-orange-900/20 dark:border-orange-900/30'}`}>
                                                                    {list.status === 'completed' ? 'Tamamlandı' : 'Bekliyor'}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="flex flex-wrap gap-2 relative z-10 mb-4">
                                                        {list.items?.slice(0, 5).map((item, idx) => (
                                                            <span key={idx} className="text-[10px] font-bold bg-white dark:bg-slate-900 px-3 py-1.5 rounded-xl border border-gray-100 dark:border-slate-700 text-gray-600 dark:text-slate-400">
                                                                {item.ingredientName} {getShoppingPreviewAmount(item) ? `(${getShoppingPreviewAmount(item)})` : ''}
                                                            </span>
                                                        ))}
                                                        {list.totalItemsCached > 5 && (
                                                            <span className="text-[10px] font-black text-primary px-2 py-1.5">+{list.totalItemsCached - 5} daha</span>
                                                        )}
                                                    </div>
                                                    {isShoppingListPreview(list) && (
                                                        <div className="mb-3 rounded-2xl border border-blue-100 bg-blue-50 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.12em] text-blue-700 dark:border-blue-900/30 dark:bg-blue-900/10 dark:text-blue-300">
                                                            Alakart listesi senkronlanıyor. Detaylar gerçek liste kaydı gelir gelmez açılacak.
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="mt-6 pt-5 bg-white/30 dark:bg-slate-900/30 rounded-2xl border border-white/50 dark:border-slate-800/50 p-4 flex justify-between items-center relative z-10">
                                                    <div className="flex items-center gap-2 text-slate-400">
                                                        <span className="material-symbols-outlined text-sm">visibility</span>
                                                        <span className="text-[10px] font-black uppercase tracking-widest">{isShoppingListPreview(list) ? 'Senkron bekleniyor' : 'Detayları Gör'}</span>
                                                    </div>

                                                    <div className="flex flex-col items-end">
                                                        <span className="text-[8px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1">Toplam Tahmin</span>
                                                        <div className="flex items-center gap-1.5">
                                                            <span className="text-xl font-black text-[#ff3b3b] dark:text-[#ff6b6b] leading-none">
                                                                {formatCurrency(resolveProjectionTotalPrice(list))}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        ))}

                                        {hasMore && (
                                            <div className="col-span-full flex justify-center mt-4 mb-4">
                                                <button
                                                    onClick={() => fetchLists(true)}
                                                    disabled={isLoading}
                                                    className="px-8 py-3 bg-gray-100 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-2xl font-black text-[10px] uppercase tracking-widest text-slate-500 hover:text-primary transition-all disabled:opacity-50"
                                                >
                                                    {isLoading ? 'Yükleniyor...' : 'Daha Fazla Yükle'}
                                                </button>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                            <div className="p-8 pt-4 bg-gray-50/50 dark:bg-slate-900/50 border-t border-gray-100 dark:border-slate-800">
                                <button
                                    onClick={() => setIsHistoryViewOpen(false)}
                                    className="w-full h-14 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-2xl font-black text-xs uppercase tracking-widest text-gray-500 hover:text-primary hover:border-primary/30 transition-all shadow-sm active:scale-95"
                                >
                                    Kapat
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Shopping List Detail Modal */}
            <ShoppingListDetailModal
                list={selectedList}
                isOpen={!!selectedList}
                autoStartFinalizing={selectedListAutoReview}
                onClose={() => {
                    setSelectedList(null);
                    setSelectedListAutoReview(false);
                }}
                onComplete={() => {
                    fetchLists();
                    setSelectedList(null);
                    setSelectedListAutoReview(false);
                }}
                onDeleteList={() => {
                    handleDeleteList(selectedList.id);
                    setSelectedList(null);
                    setSelectedListAutoReview(false);
                }}
            />

            {/* Alternative Product Modal (from card) */}
            {sharingList && (
                <AlternativeProductModal
                    listId={sharingList.id}
                    items={sharingList.items || []}
                    onClose={() => setSharingList(null)}
                    onShareGenerated={(shareUrl) => {
                        setSharingList(null);
                        Swal.fire({
                            icon: 'success',
                            title: 'Paylaşım Linki Oluşturuldu!',
                            html: `<p class="mb-4">Link kopyalandı ve 24 saat geçerli.</p><input type="text" value="${shareUrl}" readonly class="w-full p-2 border rounded text-sm" />`,
                            confirmButtonColor: 'var(--primary-color)'
                        });
                    }}
                />
            )}
        </div>
    );
};

export default MyShoppingLists;
